import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { prospectsAPI, prospectClassesAPI, usersAPI, searchAPI, teamsV2API } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Loader2, Building2, User, MapPin, Briefcase, Search, Zap, HardHat, CheckCircle, X, Building, MapPinned, Users, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function ProspectForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const isEditing = Boolean(id);
    const searchTimeoutRef = useRef(null);
    const citySearchTimeoutRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [prospectClasses, setProspectClasses] = useState({});
    const [teamMembers, setTeamMembers] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [subclasses, setSubclasses] = useState([]);
    
    // Nouvelles données pour Full Admin
    const [allVPs, setAllVPs] = useState([]);
    const [allApporteurs, setAllApporteurs] = useState([]);
    const [attributionMode, setAttributionMode] = useState('myself'); // 'myself', 'vp', 'apporteur'
    const [selectedVPId, setSelectedVPId] = useState('');
    const [selectedApporteurId, setSelectedApporteurId] = useState('');
    const [vpTeams, setVpTeams] = useState([]); // Équipes du VP sélectionné
    const [selectedVPTeamId, setSelectedVPTeamId] = useState(''); // Équipe choisie pour le VP
    
    // Recherche entreprise - visible directement
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const [companySearchResults, setCompanySearchResults] = useState([]);
    const [searchingCompany, setSearchingCompany] = useState(false);
    const [showCompanyResults, setShowCompanyResults] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    
    // Recherche villes
    const [citiesForPostalCode, setCitiesForPostalCode] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    
    const [formData, setFormData] = useState({
        first_name: '', last_name: '', company_name: '', siret: '', siren: '',
        email: '', phone: '', address: '', address2: '', city: '', postal_code: '',
        country: 'France', naf_code: '', website: '', site_description: '', prospect_class: '',
        prospect_subclass: '', subclass_other: '', notes: '', assigned_to: '',
        create_easy_power: true, create_chantier: false
    });

    // Déterminer les options d'attribution selon le rôle
    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = ((['admin_full', 'admin'].includes(user?.role) || user?.is_admin === true) || user?.is_admin === true);
    const isVPS = user?.role === 'vp_superviseur';
    const isVPP = user?.role === 'vp_payeur';
    const isVP = user?.role === 'vp';  // VP générique
    const isApporteur = user?.role === 'apporteur';
    
    // Choix d'attribution pour VP: 'myself' (VP Direct) ou 'apporteur' (équipe)
    const [vpAttributionMode, setVpAttributionMode] = useState('myself');
    // Liste des apporteurs du VP
    const [vpApporteurs, setVpApporteurs] = useState([]);
    const [selectedVpApporteurId, setSelectedVpApporteurId] = useState('');
    // Pourcentage que le VP laisse à son apporteur (modulable par le VP).
    // Valeur vide '' = utiliser le défaut équipe côté backend.
    const [vpApporteurPct, setVpApporteurPct] = useState('');

    useEffect(() => { loadInitialData(); }, [id]);
    
    // Charger les apporteurs du VP connecté
    useEffect(() => {
        const loadVpApporteurs = async () => {
            if (isVP && user?.id) {
                try {
                    // Charger les apporteurs de TOUTES les équipes du VP (payeur ou supervisée)
                    const response = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/dossier/apporteurs-disponibles/${user.id}`,
                        { headers: { 'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}` } }
                    );
                    const data = await response.json();
                    const apporteurs = Array.isArray(data) ? data.filter(t => t.type === 'apporteur') : [];
                    setVpApporteurs(apporteurs);
                } catch (error) {
                    console.error('Erreur chargement apporteurs VP:', error);
                }
            }
        };
        loadVpApporteurs();
    }, [isVP, user?.id]);

    useEffect(() => {
        if (formData.prospect_class && prospectClasses[formData.prospect_class]) {
            setSubclasses(prospectClasses[formData.prospect_class].subclasses || []);
        } else {
            setSubclasses([]);
        }
    }, [formData.prospect_class, prospectClasses]);

    // Recherche entreprise avec debounce
    useEffect(() => {
        if (companySearchQuery.length >= 3) {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = setTimeout(() => {
                searchCompanies(companySearchQuery);
            }, 400);
        } else {
            setCompanySearchResults([]);
            setShowCompanyResults(false);
        }
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [companySearchQuery]);

    // Recherche villes avec debounce
    useEffect(() => {
        if (formData.postal_code?.length === 5) {
            if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
            citySearchTimeoutRef.current = setTimeout(() => {
                searchCitiesByPostalCode(formData.postal_code);
            }, 300);
        } else {
            setCitiesForPostalCode([]);
        }
        return () => { if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current); };
    }, [formData.postal_code]);

    // Quand un VP est sélectionné, mettre à jour ses équipes disponibles
    useEffect(() => {
        if (selectedVPId && allVPs.length > 0) {
            const selectedVP = allVPs.find(vp => vp.id === selectedVPId);
            if (selectedVP?.teams && selectedVP.teams.length > 0) {
                setVpTeams(selectedVP.teams);
                // Si une seule équipe, la sélectionner automatiquement
                if (selectedVP.teams.length === 1) {
                    setSelectedVPTeamId(selectedVP.teams[0].id);
                } else {
                    setSelectedVPTeamId(''); // Plusieurs équipes, l'utilisateur doit choisir
                }
            } else {
                setVpTeams([]);
                setSelectedVPTeamId('');
            }
        } else {
            setVpTeams([]);
            setSelectedVPTeamId('');
        }
    }, [selectedVPId, allVPs]);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const classesRes = await prospectClassesAPI.getAll();
            setProspectClasses(classesRes.data);
            
            // Charger les membres d'équipe selon le rôle
            if (isVPP || isVPS) {
                // VP Payeur ou Superviseur : chercher leur équipe V2
                try {
                    const teamsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams-v2`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}` }
                    });
                    const teamsData = await teamsRes.json();
                    
                    // Trouver l'équipe dont ce VP fait partie
                    const myTeam = teamsData.find(t => 
                        (isVPP && t.vp_payeur?.id === user.id) ||
                        (isVPS && t.vp_superviseur?.id === user.id)
                    );
                    
                    if (myTeam) {
                        const members = [];
                        // VPS peut attribuer à VPP et apporteurs
                        if (isVPS && myTeam.vp_payeur) {
                            members.push({ ...myTeam.vp_payeur, _role: 'VP Payeur' });
                        }
                        // VPP et VPS peuvent attribuer aux apporteurs
                        if (myTeam.apporteurs) {
                            myTeam.apporteurs.forEach(a => members.push({ ...a, _role: 'Apporteur' }));
                        }
                        setTeamMembers(members);
                    }
                } catch (e) {
                    console.error('Erreur chargement équipe VP:', e);
                }
            } else if (isAdminFull) {
                // Full Admin : charger VPs et Apporteurs séparément
                try {
                    const [usersRes, teamsRes] = await Promise.all([
                        usersAPI.getAll(),
                        teamsV2API.getAll()
                    ]);
                    
                    const users = usersRes.data || [];
                    const teams = teamsRes.data || [];
                    
                    // Filtrer les VPs actifs ET enrichir avec leurs équipes
                    const vps = users.filter(u => u.is_active && u.role === 'vp').map(vp => {
                        // Trouver TOUTES les équipes de ce VP (comme payer OU supervisor)
                        const vpTeamsList = teams.filter(t => 
                            t.payer_vp_id === vp.id || t.supervisor_vp_id === vp.id
                        ).map(t => ({
                            id: t.id,
                            name: t.name,
                            role_in_team: t.payer_vp_id === vp.id ? 'VP Payeur' : 'VP Superviseur'
                        }));
                        return { ...vp, teams: vpTeamsList };
                    });
                    setAllVPs(vps);
                    
                    // Filtrer les Apporteurs actifs avec leur équipe (via leur VP)
                    const apporteurs = users.filter(u => u.is_active && u.role === 'apporteur').map(a => {
                        // CORRECTION: Trouver l'équipe via le VP de l'apporteur (pas via member_ids)
                        const team = teams.find(t => t.payer_vp_id === a.vp_id);
                        return { 
                            ...a, 
                            team_name: team?.name || 'Sans équipe', 
                            team_id: team?.id
                        };
                    });
                    setAllApporteurs(apporteurs);
                    setAllTeams(teams);
                } catch (e) {
                    console.error('Erreur chargement données admin:', e);
                }
            } else if (isAdmin) {
                // Admin (non full) : charger toutes les équipes V2
                try {
                    const teamsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams-v2`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}` }
                    });
                    const teamsData = await teamsRes.json();
                    setAllTeams(teamsData || []);
                } catch (e) {
                    console.error('Erreur chargement équipes:', e);
                }
            }
            // Apporteur : pas besoin de charger d'équipe, le dossier est pour lui-même
            
            if (isEditing) {
                const prospectRes = await prospectsAPI.getById(id);
                const prospectData = prospectRes.data;
                setFormData({ ...prospectData, create_easy_power: !!prospectData.easy_power_stage, create_chantier: !!prospectData.chantier_stage });
                
                // Pour édition par Full Admin : déterminer le mode d'attribution actuel
                if (isAdminFull) {
                    if (prospectData.dossier_apporteur_id) {
                        setAttributionMode('apporteur');
                        setSelectedApporteurId(prospectData.dossier_apporteur_id);
                    } else if (prospectData.dossier_vp_id) {
                        setAttributionMode('vp');
                        setSelectedVPId(prospectData.dossier_vp_id);
                    } else {
                        setAttributionMode('myself');
                    }
                }
                
                // Pour édition par VP : pré-remplir le mode d'attribution
                // - Si un apporteur est rattaché : mode "apporteur"
                // - Sinon : mode "myself" (VP direct)
                if (isVP) {
                    if (prospectData.dossier_apporteur_id) {
                        setVpAttributionMode('apporteur');
                        setSelectedVpApporteurId(prospectData.dossier_apporteur_id);
                        // Pré-remplir le % apporteur si existant (sinon laisser vide = défaut équipe)
                        if (prospectData.apporteur_commission_percent != null) {
                            setVpApporteurPct(prospectData.apporteur_commission_percent);
                        }
                    } else {
                        setVpAttributionMode('myself');
                    }
                }
            }
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const searchCompanies = async (query) => {
        setSearchingCompany(true);
        try {
            const res = await searchAPI.companies(query);
            setCompanySearchResults(res.data.results || []);
            setShowCompanyResults(true);
        } catch (e) { 
            setCompanySearchResults([]); 
        }
        finally { setSearchingCompany(false); }
    };

    const searchCitiesByPostalCode = async (postalCode) => {
        setLoadingCities(true);
        try {
            const res = await searchAPI.citiesByPostalCode(postalCode);
            const cities = res.data.cities || [];
            setCitiesForPostalCode(cities);
            if (cities.length === 1) {
                setFormData(prev => ({ ...prev, city: cities[0].name }));
            }
        } catch (e) { setCitiesForPostalCode([]); }
        finally { setLoadingCities(false); }
    };

    const handleCompanySelect = (company) => {
        setFormData(prev => ({
            ...prev,
            company_name: company.company_name || prev.company_name,
            siret: company.siret || prev.siret,
            siren: company.siren || prev.siren,
            address: company.address || prev.address,
            postal_code: company.postal_code || prev.postal_code,
            city: company.city || prev.city,
            naf_code: company.naf_code || prev.naf_code
        }));
        setSelectedCompany(company);
        setShowCompanyResults(false);
        setCompanySearchQuery('');
        toast.success('Entreprise selectionnee - champs remplis automatiquement');
    };

    const clearSelectedCompany = () => {
        setSelectedCompany(null);
        setFormData(prev => ({
            ...prev,
            company_name: '', siret: '', siren: '', address: '', 
            postal_code: '', city: '', naf_code: ''
        }));
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleSelectChange = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));
    const handleCheckboxChange = (name, checked) => setFormData(prev => ({ ...prev, [name]: checked }));
    const isOtherSubclass = () => formData.prospect_subclass?.includes('_autre') || formData.prospect_subclass === 'autre';

    // Fonction pour créer le prospect (normale ou forcée)
    const createProspect = async (forceCreate = false) => {
        if (!formData.prospect_class) { toast.error('Selectionnez une classe'); return; }
        if (!formData.prospect_subclass) { toast.error('Selectionnez une sous-classe'); return; }
        if (isOtherSubclass() && !formData.subclass_other) { toast.error('Precisez le type "Autre"'); return; }
        if (!isEditing && !formData.create_easy_power && !formData.create_chantier) { toast.error('Selectionnez au moins un pipeline'); return; }
        
        // Validation pour mode VP Direct (Admin Full): juste vérifier qu'un VP est sélectionné
        if (isAdminFull && attributionMode === 'vp' && !selectedVPId) {
            toast.error('Veuillez sélectionner un VP.');
            return;
        }
        
        // Validation pour mode Apporteur (Admin Full): un apporteur doit être sélectionné
        if (isAdminFull && attributionMode === 'apporteur' && !selectedApporteurId) {
            toast.error('Veuillez sélectionner un apporteur.');
            return;
        }
        
        // Validation pour VP: si mode apporteur, un apporteur doit être sélectionné
        if (isVP && vpAttributionMode === 'apporteur' && !selectedVpApporteurId) {
            toast.error('Veuillez sélectionner un apporteur.');
            return;
        }
        
        setSaving(true);
        
        try {
            const dataToSend = { ...formData };
            if (!isOtherSubclass()) dataToSend.subclass_other = null;
            
            // Si force_create est activé (Admin Full uniquement)
            if (forceCreate && isAdminFull) {
                dataToSend.force_create = true;
            }
            
            // Gestion attribution pour Full Admin (nouvelles règles)
            if (isAdminFull) {
                // Réinitialiser les champs d'attribution
                dataToSend.dossier_vp_id = null;
                dataToSend.dossier_apporteur_id = null;
                dataToSend.team_id = null;
                dataToSend.attribution_mode = attributionMode;
                
                if (attributionMode === 'myself') {
                    // Moi-même : 50/50 Valnexo/AT Service, pas de VP
                    // Rien à définir, le backend gérera
                } else if (attributionMode === 'vp' && selectedVPId) {
                    // VP seul : commission du VP s'applique
                    dataToSend.dossier_vp_id = selectedVPId;
                    // Mode VP Direct: pas de team_id
                } else if (attributionMode === 'apporteur' && selectedApporteurId) {
                    // Mode Apporteur: on envoie l'ID de l'apporteur
                    // Le backend calculera le VP et l'équipe via le vp_id de l'apporteur
                    dataToSend.dossier_apporteur_id = selectedApporteurId;
                    // team_id sera calculé par le backend
                }
            } else if (isVP) {
                // ============== CHOIX VP: Pour moi-même OU pour un apporteur ==============
                // En création : on envoie dossier_vp_id (le VP est le VP du dossier)
                // En édition : on NE peut PAS modifier le VP du dossier (réservé admin_full)
                if (!isEditing) {
                    dataToSend.dossier_vp_id = user.id;
                }
                
                if (vpAttributionMode === 'myself') {
                    // VP Direct: pas d'apporteur, pas d'équipe
                    if (!isEditing) {
                        dataToSend.attribution_mode = 'vp';
                    }
                    // En édition : on ne peut pas "retirer" un apporteur — réservé admin
                    if (!isEditing) {
                        dataToSend.dossier_apporteur_id = null;
                    }
                } else if (vpAttributionMode === 'apporteur' && selectedVpApporteurId) {
                    // Pour un apporteur: logique équipe normale
                    if (!isEditing) {
                        dataToSend.attribution_mode = 'apporteur';
                    }
                    dataToSend.dossier_apporteur_id = selectedVpApporteurId;
                    // VP peut moduler la part qu'il laisse à son apporteur
                    if (vpApporteurPct !== '' && vpApporteurPct !== null && !isNaN(parseFloat(vpApporteurPct))) {
                        dataToSend.apporteur_commission_percent = parseFloat(vpApporteurPct);
                    }
                    // team_id sera déterminé par le backend via le VP
                }
            } else {
                // Ancien comportement pour les autres rôles (Admin, Apporteur)
                if (dataToSend.assigned_to) {
                    const assignedMember = teamMembers.find(m => m.id === dataToSend.assigned_to);
                    if (assignedMember) {
                        if (assignedMember.role === 'apporteur') {
                            dataToSend.dossier_apporteur_id = dataToSend.assigned_to;
                        } else if (assignedMember.role === 'vp') {
                            if (assignedMember._role === 'VP Payeur' || assignedMember._role?.includes('Payeur')) {
                                dataToSend.dossier_vp_id = dataToSend.assigned_to;
                            } else {
                                dataToSend.dossier_vp_2_id = dataToSend.assigned_to;
                            }
                        }
                    }
                }
            }
            delete dataToSend.assigned_to;
            
            if (isEditing) {
                delete dataToSend.create_easy_power;
                delete dataToSend.create_chantier;
                delete dataToSend.force_create;
                await prospectsAPI.update(id, dataToSend);
                toast.success('Prospect mis a jour');
            } else {
                const response = await prospectsAPI.create(dataToSend);
                
                // Vérifier si un avertissement de doublon est retourné
                if (response.data?._duplicate_warning) {
                    const warning = response.data._duplicate_warning;
                    toast.warning(
                        <div className="space-y-1">
                            <div className="font-semibold">⚠️ Prospect similaire détecté</div>
                            <div className="text-sm">{warning.message}</div>
                        </div>,
                        { duration: 8000 }
                    );
                }
                
                toast.success('Prospect créé avec succès');
            }
            navigate('/prospects');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally { setSaving(false); }
    };

    // Handler pour le formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();
        await createProspect(false);
    };

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
                    <h1 className="text-2xl font-outfit font-semibold">{isEditing ? 'Modifier le Prospect' : 'Nouveau Prospect'}</h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* RECHERCHE ENTREPRISE - BIEN VISIBLE */}
                    {!isEditing && (
                        <Card className="border-2 border-blue-400 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-xl text-blue-700">
                                    <Search className="w-6 h-6" />
                                    Rechercher une Entreprise
                                </CardTitle>
                                <CardDescription className="text-blue-600">
                                    Tapez le <strong>nom de l'entreprise</strong>, le <strong>SIRET</strong> (14 chiffres) ou le <strong>SIREN</strong> (9 chiffres)
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Entreprise selectionnee */}
                                {selectedCompany && (
                                    <div className="p-4 bg-green-100 border-2 border-green-400 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <CheckCircle className="w-6 h-6 text-green-600" />
                                                <div>
                                                    <p className="font-bold text-green-800">{selectedCompany.company_name}</p>
                                                    <p className="text-sm text-green-700">SIRET: {selectedCompany.siret} | {selectedCompany.city}</p>
                                                </div>
                                            </div>
                                            <Button variant="ghost" size="sm" onClick={clearSelectedCompany} className="text-green-700 hover:text-red-600">
                                                <X className="w-5 h-5" />
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Champ de recherche */}
                                {!selectedCompany && (
                                    <div className="relative">
                                        <div className="flex gap-2">
                                            <div className="relative flex-1">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                                                <Input
                                                    type="text"
                                                    value={companySearchQuery}
                                                    onChange={(e) => setCompanySearchQuery(e.target.value)}
                                                    placeholder="Ex: Carrefour, 12345678901234, 123456789..."
                                                    className="pl-10 h-12 text-lg border-2 border-blue-300 focus:border-blue-500 bg-white"
                                                    data-testid="company-search-input"
                                                />
                                                {searchingCompany && (
                                                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-blue-500" />
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Resultats de recherche */}
                                        {showCompanyResults && companySearchResults.length > 0 && (
                                            <div className="absolute z-50 w-full mt-2 bg-white border-2 border-blue-300 rounded-lg shadow-xl max-h-80 overflow-y-auto">
                                                <div className="p-2 bg-blue-50 border-b text-sm text-blue-600 font-medium">
                                                    {companySearchResults.length} entreprise(s) trouvee(s)
                                                </div>
                                                {companySearchResults.map((company, idx) => (
                                                    <div
                                                        key={company.siret || idx}
                                                        onClick={() => handleCompanySelect(company)}
                                                        className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                                                        data-testid={`company-result-${idx}`}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex items-start gap-3">
                                                                <Building className="w-5 h-5 text-blue-500 mt-1 shrink-0" />
                                                                <div>
                                                                    <p className="font-semibold text-slate-800">{company.company_name}</p>
                                                                    <p className="text-sm text-slate-600">{company.address}</p>
                                                                    <p className="text-sm text-slate-500">{company.postal_code} {company.city}</p>
                                                                </div>
                                                            </div>
                                                            <Badge variant="outline" className="shrink-0 bg-blue-50 text-blue-700 border-blue-300">
                                                                {company.siret}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {showCompanyResults && companySearchQuery.length >= 3 && companySearchResults.length === 0 && !searchingCompany && (
                                            <div className="absolute z-50 w-full mt-2 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                                                <p className="text-amber-700">Aucune entreprise trouvee. Vous pouvez remplir les champs manuellement ci-dessous.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                <p className="text-xs text-blue-500">
                                    Source: Base officielle SIRENE (INSEE) - Donnees des entreprises francaises
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Type de dossier */}
                    {!isEditing && (
                        <Card className="border-amber-200 bg-amber-50/50">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-amber-600" />Type de dossier a creer</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <button 
                                        type="button"
                                        className={cn("flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all select-none text-left", formData.create_easy_power ? "border-amber-500 bg-amber-100" : "border-slate-200 hover:border-amber-300")} 
                                        onClick={() => setFormData(prev => ({ ...prev, create_easy_power: !prev.create_easy_power }))} 
                                        data-testid="pipeline-easy-power"
                                    >
                                        <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center", formData.create_easy_power ? "bg-amber-500 border-amber-500" : "border-slate-300")}>
                                            {formData.create_easy_power && <CheckCircle className="w-3 h-3 text-white" />}
                                        </div>
                                        <Zap className={cn("w-5 h-5", formData.create_easy_power ? "text-amber-600" : "text-slate-400")} />
                                        <span className="font-medium">Easy Power</span>
                                    </button>
                                    <button 
                                        type="button"
                                        className={cn("flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all select-none text-left", formData.create_chantier ? "border-blue-500 bg-blue-100" : "border-slate-200 hover:border-blue-300")} 
                                        onClick={() => setFormData(prev => ({ ...prev, create_chantier: !prev.create_chantier }))} 
                                        data-testid="pipeline-chantier"
                                    >
                                        <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center", formData.create_chantier ? "bg-blue-500 border-blue-500" : "border-slate-300")}>
                                            {formData.create_chantier && <CheckCircle className="w-3 h-3 text-white" />}
                                        </div>
                                        <HardHat className={cn("w-5 h-5", formData.create_chantier ? "text-blue-600" : "text-slate-400")} />
                                        <span className="font-medium">Chantier</span>
                                    </button>
                                    <button 
                                        type="button"
                                        className={cn("flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all select-none text-left", formData.create_easy_power && formData.create_chantier ? "border-purple-500 bg-purple-100" : "border-slate-200 hover:border-purple-300")} 
                                        onClick={() => { const both = !(formData.create_easy_power && formData.create_chantier); setFormData(prev => ({ ...prev, create_easy_power: both, create_chantier: both })); }} 
                                        data-testid="pipeline-both"
                                    >
                                        <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center", formData.create_easy_power && formData.create_chantier ? "bg-purple-500 border-purple-500" : "border-slate-300")}>
                                            {formData.create_easy_power && formData.create_chantier && <CheckCircle className="w-3 h-3 text-white" />}
                                        </div>
                                        <CheckCircle className={cn("w-5 h-5", formData.create_easy_power && formData.create_chantier ? "text-purple-600" : "text-slate-400")} />
                                        <span className="font-medium">Les deux</span>
                                    </button>
                                </div>
                                {!formData.create_easy_power && !formData.create_chantier && <p className="text-sm text-red-600 mt-2">Selectionnez au moins un type de dossier.</p>}
                            </CardContent>
                        </Card>
                    )}

                    {/* Attribution du dossier - selon le rôle
                        - admin_full : toujours visible (création ET édition)
                        - vp        : création + édition (peut réassigner l'apporteur)
                        - autres (apporteur, comptable) : jamais
                        - autres rôles non-VP : uniquement en création */}
                    {!isApporteur && (isAdminFull || isVP || !isEditing) && (
                        <Card className="border-purple-200 bg-purple-50/50">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2">
                                    <User className="w-5 h-5 text-purple-600" />
                                    Attribution du dossier
                                </CardTitle>
                                <CardDescription>
                                    {isAdminFull ? "Choisissez à qui attribuer ce dossier (modifiable uniquement par Full Admin)" : 
                                     isAdmin ? "Choisissez à qui attribuer ce dossier" :
                                     isVP && isEditing ? "Modifiez l'apporteur de votre équipe attribué à ce dossier" :
                                     isVPS ? "Attribuez le dossier à vous-même ou à un membre de votre équipe" :
                                     isVPP ? "Attribuez le dossier à vous-même ou à un apporteur de votre équipe" :
                                     isVP ? "Attribuez le dossier à vous-même ou à un apporteur de votre équipe" : ""}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                
                                {/* Full Admin : 3 options claires */}
                                {isAdminFull && (
                                    <div className="space-y-4">
                                        {/* Option 1 : Moi-même (50/50) */}
                                        <div 
                                            className={cn(
                                                "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                attributionMode === 'myself' ? "border-purple-500 bg-purple-100" : "border-slate-200 hover:border-purple-300"
                                            )}
                                            onClick={() => {
                                                setAttributionMode('myself');
                                                setSelectedVPId('');
                                                setSelectedApporteurId('');
                                            }}
                                            data-testid="attribution-myself"
                                        >
                                            <div className={cn(
                                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                                attributionMode === 'myself' ? "bg-purple-500 border-purple-500" : "border-slate-300"
                                            )}>
                                                {attributionMode === 'myself' && <div className="w-2 h-2 bg-white rounded-full" />}
                                            </div>
                                            <User className={cn("w-5 h-5", attributionMode === 'myself' ? "text-purple-600" : "text-slate-400")} />
                                            <div className="flex-1">
                                                <span className="font-medium">Moi-même</span>
                                                <p className="text-xs text-slate-500">50% Valnexo / 50% AT Service (pas de VP)</p>
                                            </div>
                                        </div>
                                        
                                        {/* Option 2 : VP */}
                                        <div 
                                            className={cn(
                                                "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                attributionMode === 'vp' ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-amber-300"
                                            )}
                                            onClick={() => {
                                                setAttributionMode('vp');
                                                setSelectedApporteurId('');
                                            }}
                                            data-testid="attribution-vp"
                                        >
                                            <div className={cn(
                                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                                attributionMode === 'vp' ? "bg-amber-500 border-amber-500" : "border-slate-300"
                                            )}>
                                                {attributionMode === 'vp' && <div className="w-2 h-2 bg-white rounded-full" />}
                                            </div>
                                            <UserCheck className={cn("w-5 h-5", attributionMode === 'vp' ? "text-amber-600" : "text-slate-400")} />
                                            <div className="flex-1">
                                                <span className="font-medium">VP Direct</span>
                                                <p className="text-xs text-slate-500">Pas d'équipe, pas d'apporteur - Commission VP uniquement</p>
                                            </div>
                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">🎯 Direct</span>
                                        </div>
                                        
                                        {/* Sélection VP si option VP choisie */}
                                        {attributionMode === 'vp' && allVPs.length > 0 && (
                                            <div className="ml-8 space-y-3 p-3 bg-amber-50/50 rounded-lg border border-amber-200">
                                                <div className="text-xs text-amber-700 font-medium mb-2">
                                                    ⚠️ Mode VP Direct : Le dossier sera attribué exclusivement au VP sélectionné, sans équipe ni apporteur.
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Sélectionner un VP</Label>
                                                    <Select value={selectedVPId} onValueChange={setSelectedVPId}>
                                                        <SelectTrigger data-testid="select-vp">
                                                            <SelectValue placeholder="Choisir un VP..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {allVPs.map((vp) => (
                                                                <SelectItem key={vp.id} value={vp.id}>
                                                                    <div className="flex items-center gap-2">
                                                                        <span>{vp.first_name} {vp.last_name}</span>
                                                                        {vp.vp_direct_pct && <span className="text-slate-400">({vp.vp_direct_pct}%)</span>}
                                                                    </div>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                {/* En mode VP Direct, pas de sélection d'équipe */}
                                                {selectedVPId && (
                                                    <div className="text-sm text-amber-700 flex items-center gap-2 p-2 bg-amber-100/50 rounded">
                                                        <span>✓ Commission VP appliquée : {allVPs.find(v => v.id === selectedVPId)?.vp_direct_pct || 30}%</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Option 3 : Apporteur */}
                                        <div 
                                            className={cn(
                                                "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                attributionMode === 'apporteur' ? "border-blue-500 bg-blue-100" : "border-slate-200 hover:border-blue-300"
                                            )}
                                            onClick={() => {
                                                setAttributionMode('apporteur');
                                                setSelectedVPId('');
                                            }}
                                            data-testid="attribution-apporteur"
                                        >
                                            <div className={cn(
                                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                                attributionMode === 'apporteur' ? "bg-blue-500 border-blue-500" : "border-slate-300"
                                            )}>
                                                {attributionMode === 'apporteur' && <div className="w-2 h-2 bg-white rounded-full" />}
                                            </div>
                                            <Users className={cn("w-5 h-5", attributionMode === 'apporteur' ? "text-blue-600" : "text-slate-400")} />
                                            <div className="flex-1">
                                                <span className="font-medium">Apporteur</span>
                                                <p className="text-xs text-slate-500">Règles de l'équipe de l'apporteur s'appliquent</p>
                                            </div>
                                        </div>
                                        
                                        {/* Sélection Apporteur si option Apporteur choisie */}
                                        {attributionMode === 'apporteur' && allApporteurs.length > 0 && (
                                            <div className="ml-8 space-y-3">
                                                <div className="space-y-2">
                                                    <Label>Sélectionner un Apporteur</Label>
                                                    <Select value={selectedApporteurId} onValueChange={setSelectedApporteurId}>
                                                        <SelectTrigger data-testid="select-apporteur">
                                                            <SelectValue placeholder="Choisir un apporteur..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {allApporteurs.map((apporteur) => (
                                                                <SelectItem key={apporteur.id} value={apporteur.id}>
                                                                    <div className="flex items-center gap-2">
                                                                        <span>{apporteur.first_name} {apporteur.last_name}</span>
                                                                        <Badge variant="outline" className="text-xs">
                                                                            {apporteur.team_name}
                                                                        </Badge>
                                                                    </div>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                
                                                {/* Afficher l'équipe de l'apporteur sélectionné */}
                                                {selectedApporteurId && (
                                                    <div className="text-sm text-slate-600 flex items-center gap-2">
                                                        <Users className="w-4 h-4" />
                                                        Équipe : <Badge variant="outline">
                                                            {allApporteurs.find(a => a.id === selectedApporteurId)?.team_name || 'Non définie'}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {/* VP : Choix entre VP Direct ou Apporteur (création ET édition) */}
                                {isVP && (
                                    <div className="space-y-4">
                                        {/* Option 1 : Pour moi-même (VP Direct) */}
                                        <div 
                                            className={cn(
                                                "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                vpAttributionMode === 'myself' ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-amber-300"
                                            )}
                                            onClick={() => {
                                                setVpAttributionMode('myself');
                                                setSelectedVpApporteurId('');
                                            }}
                                            data-testid="vp-attribution-myself"
                                        >
                                            <div className={cn(
                                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                                vpAttributionMode === 'myself' ? "bg-amber-500 border-amber-500" : "border-slate-300"
                                            )}>
                                                {vpAttributionMode === 'myself' && <div className="w-2 h-2 bg-white rounded-full" />}
                                            </div>
                                            <UserCheck className={cn("w-5 h-5", vpAttributionMode === 'myself' ? "text-amber-600" : "text-slate-400")} />
                                            <div className="flex-1">
                                                <span className="font-medium">Pour moi-même</span>
                                                <p className="text-xs text-slate-500">Dossier VP Direct - Pas d'équipe, pas d'apporteur</p>
                                            </div>
                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">🎯 VP Direct</span>
                                        </div>
                                        
                                        {/* Option 2 : Pour un apporteur */}
                                        <div 
                                            className={cn(
                                                "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                vpAttributionMode === 'apporteur' ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-blue-300"
                                            )}
                                            onClick={() => setVpAttributionMode('apporteur')}
                                            data-testid="vp-attribution-apporteur"
                                        >
                                            <div className={cn(
                                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                                vpAttributionMode === 'apporteur' ? "bg-blue-500 border-blue-500" : "border-slate-300"
                                            )}>
                                                {vpAttributionMode === 'apporteur' && <div className="w-2 h-2 bg-white rounded-full" />}
                                            </div>
                                            <Users className={cn("w-5 h-5", vpAttributionMode === 'apporteur' ? "text-blue-600" : "text-slate-400")} />
                                            <div className="flex-1">
                                                <span className="font-medium">Pour un apporteur</span>
                                                <p className="text-xs text-slate-500">Logique équipe normale - Commission selon les règles d'équipe</p>
                                            </div>
                                        </div>
                                        
                                        {/* Sélection Apporteur si option Apporteur choisie */}
                                        {vpAttributionMode === 'apporteur' && (
                                            <div className="ml-8 space-y-3">
                                                {vpApporteurs.length > 0 ? (
                                                    <>
                                                        <div className="space-y-2">
                                                            <Label>Sélectionner un de vos apporteurs</Label>
                                                            <Select value={selectedVpApporteurId} onValueChange={setSelectedVpApporteurId}>
                                                                <SelectTrigger data-testid="vp-select-apporteur">
                                                                    <SelectValue placeholder="Choisir un apporteur..." />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {vpApporteurs.map((apporteur) => (
                                                                        <SelectItem key={apporteur.id} value={apporteur.id}>
                                                                            {apporteur.first_name} {apporteur.last_name}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        
                                                        {selectedVpApporteurId && (
                                                            <div className="space-y-3">
                                                                <div className="text-sm text-blue-700 flex items-center gap-2 p-2 bg-blue-50 rounded">
                                                                    <span>✓ Commission équipe appliquée</span>
                                                                </div>
                                                                {/* VP : module la part qu'il laisse à son apporteur — soit en %, soit en € */}
                                                                {(() => {
                                                                    // Calcul du VP brut total à partir des données du dossier
                                                                    const currentPct = parseFloat(formData.apporteur_commission_percent) || 0;
                                                                    const epAppCurrent = parseFloat(formData.ep_commission_apporteur) || 0;
                                                                    const chAppCurrent = parseFloat(formData.ch_commission_apporteur) || 0;
                                                                    const vpBrutEp = currentPct > 0 ? (epAppCurrent / (currentPct / 100)) : 0;
                                                                    const vpBrutCh = currentPct > 0 ? (chAppCurrent / (currentPct / 100)) : 0;
                                                                    const vpBrutTotal = vpBrutEp + vpBrutCh;

                                                                    const editedPct = (vpApporteurPct !== '' && vpApporteurPct !== null && !isNaN(parseFloat(vpApporteurPct))) ? parseFloat(vpApporteurPct) : null;
                                                                    const previewPct = editedPct !== null ? editedPct : currentPct;
                                                                    const previewAppTotal = vpBrutTotal * (previewPct / 100);
                                                                    const previewVpReste = vpBrutTotal - previewAppTotal;
                                                                    const fmt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                                                                    const canShowEuros = vpBrutTotal > 0;

                                                                    return (
                                                                        <div className="p-3 bg-white border border-blue-200 rounded space-y-3">
                                                                            <Label className="text-sm font-medium">Part de l'apporteur</Label>
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                                <div>
                                                                                    <Label htmlFor="vp-apporteur-pct" className="text-xs text-slate-500">En pourcentage (%)</Label>
                                                                                    <input
                                                                                        id="vp-apporteur-pct"
                                                                                        type="number"
                                                                                        min="0"
                                                                                        max="100"
                                                                                        step="1"
                                                                                        value={vpApporteurPct ?? ''}
                                                                                        placeholder="Ex: 50"
                                                                                        onChange={(e) => {
                                                                                            const raw = e.target.value;
                                                                                            if (raw === '') { setVpApporteurPct(''); return; }
                                                                                            const n = parseFloat(raw);
                                                                                            if (!isNaN(n) && n >= 0 && n <= 100) setVpApporteurPct(n);
                                                                                        }}
                                                                                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                                        data-testid="vp-apporteur-percent-input"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label htmlFor="vp-apporteur-eur" className={cn("text-xs", canShowEuros ? "text-slate-500" : "text-slate-300")}>
                                                                                        En euros (€) {!canShowEuros && "— pas encore calculable"}
                                                                                    </Label>
                                                                                    <input
                                                                                        id="vp-apporteur-eur"
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="1"
                                                                                        value={canShowEuros ? (editedPct !== null ? Math.round(previewAppTotal) : Math.round(currentPct * vpBrutTotal / 100)) : ''}
                                                                                        disabled={!canShowEuros}
                                                                                        placeholder={canShowEuros ? "Ex: 500" : "—"}
                                                                                        onChange={(e) => {
                                                                                            const raw = e.target.value;
                                                                                            if (raw === '' || !canShowEuros) { setVpApporteurPct(''); return; }
                                                                                            const eur = parseFloat(raw);
                                                                                            if (!isNaN(eur) && eur >= 0 && eur <= vpBrutTotal) {
                                                                                                const pct = vpBrutTotal > 0 ? (eur / vpBrutTotal) * 100 : 0;
                                                                                                setVpApporteurPct(Math.round(pct * 100) / 100);
                                                                                            }
                                                                                        }}
                                                                                        className={cn(
                                                                                            "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
                                                                                            !canShowEuros && "bg-slate-50 text-slate-400 cursor-not-allowed"
                                                                                        )}
                                                                                        data-testid="vp-apporteur-euro-input"
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            {canShowEuros && (
                                                                                <div className="flex items-center justify-between gap-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-sm">
                                                                                    <span className="text-slate-700">Reste pour vous :</span>
                                                                                    <span className="font-semibold text-emerald-700">
                                                                                        {fmt(previewVpReste)} € <span className="text-xs text-slate-500 font-normal">({(100 - previewPct).toFixed(0)}%)</span>
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                            <p className="text-[11px] text-slate-500">
                                                                                Laissez vide pour conserver le pourcentage par défaut de l'équipe.
                                                                                {canShowEuros && " La conversion € ↔ % se fait automatiquement."}
                                                                            </p>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded">
                                                        Aucun apporteur n'est assigné à vous. Utilisez "Pour moi-même" ou contactez un admin.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {/* Non Full Admin et non VP : Ancien comportement */}
                                {!isAdminFull && !isVP && !isEditing && (
                                    <>
                                        {/* Option : Dossier pour moi */}
                                        <div 
                                            className={cn(
                                                "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                !formData.assigned_to ? "border-purple-500 bg-purple-100" : "border-slate-200 hover:border-purple-300"
                                            )}
                                            onClick={() => setFormData(prev => ({ ...prev, assigned_to: '' }))}
                                        >
                                            <div className={cn(
                                                "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                                !formData.assigned_to ? "bg-purple-500 border-purple-500" : "border-slate-300"
                                            )}>
                                                {!formData.assigned_to && <div className="w-2 h-2 bg-white rounded-full" />}
                                            </div>
                                            <User className={cn("w-5 h-5", !formData.assigned_to ? "text-purple-600" : "text-slate-400")} />
                                            <span className="font-medium">Dossier pour moi</span>
                                        </div>

                                        {/* Admin (non full) : Sélection équipe puis membre */}
                                        {isAdmin && allTeams.length > 0 && (
                                            <div className="space-y-3">
                                                <Label>Ou attribuer à une équipe</Label>
                                                <Select 
                                                    value={selectedTeamId} 
                                                    onValueChange={(v) => {
                                                        setSelectedTeamId(v);
                                                        setFormData(prev => ({ ...prev, assigned_to: '' }));
                                                        const team = allTeams.find(t => t.id === v);
                                                        if (team) {
                                                            const members = [];
                                                            if (team.vp_payeur) members.push({ ...team.vp_payeur, _role: 'VP Payeur' });
                                                            if (team.vp_superviseur) members.push({ ...team.vp_superviseur, _role: 'VP Superviseur' });
                                                            if (team.apporteurs) {
                                                                team.apporteurs.forEach(a => members.push({ ...a, _role: 'Apporteur' }));
                                                            }
                                                            setTeamMembers(members);
                                                        }
                                                    }}
                                                >
                                                    <SelectTrigger><SelectValue placeholder="Sélectionnez une équipe" /></SelectTrigger>
                                                    <SelectContent>
                                                        {allTeams.map((team) => (
                                                            <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}

                                        {/* Liste des membres (VPP/VPS : leur équipe, Admin : équipe sélectionnée) */}
                                        {((isVPP || isVPS) || (isAdmin && selectedTeamId)) && teamMembers.length > 0 && (
                                            <div className="space-y-3">
                                                <Label>{isAdmin ? "Attribuer à un membre" : "Ou attribuer à un membre de votre équipe"}</Label>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {teamMembers.map((member) => {
                                                        if (isVPP && member.role !== 'apporteur') return null;
                                                        
                                                        return (
                                                            <div
                                                                key={member.id}
                                                                className={cn(
                                                                    "flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
                                                                    formData.assigned_to === member.id 
                                                                        ? "border-purple-500 bg-purple-100" 
                                                                        : "border-slate-200 hover:border-purple-300"
                                                                )}
                                                                onClick={() => setFormData(prev => ({ ...prev, assigned_to: member.id }))}
                                                            >
                                                                <div className={cn(
                                                                    "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                                                                    formData.assigned_to === member.id ? "bg-purple-500 border-purple-500" : "border-slate-300"
                                                                )}>
                                                                    {formData.assigned_to === member.id && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="font-medium text-sm truncate">{member.first_name} {member.last_name}</p>
                                                                    <p className="text-xs text-slate-500">{member._role || member.role}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Classification */}
                    <Card>
                        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5" />Classification</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Classe *</Label>
                                    <Select value={formData.prospect_class} onValueChange={(v) => handleSelectChange('prospect_class', v)}>
                                        <SelectTrigger data-testid="prospect-class-select"><SelectValue placeholder="Selectionnez une classe" /></SelectTrigger>
                                        <SelectContent>{Object.entries(prospectClasses).map(([key, val]) => <SelectItem key={key} value={key}>{val.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Sous-classe *</Label>
                                    <Select value={formData.prospect_subclass} onValueChange={(v) => handleSelectChange('prospect_subclass', v)} disabled={!formData.prospect_class}>
                                        <SelectTrigger data-testid="prospect-subclass-select"><SelectValue placeholder="Selectionnez une sous-classe" /></SelectTrigger>
                                        <SelectContent>{subclasses.map((sub) => <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {isOtherSubclass() && <div className="space-y-2"><Label>Precisez le type *</Label><Input name="subclass_other" value={formData.subclass_other} onChange={handleChange} placeholder="Decrivez le type d'activite..." /></div>}
                        </CardContent>
                    </Card>

                    {/* Informations Entreprise */}
                    <Card>
                        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" />Informations Entreprise</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Raison sociale</Label>
                                <Input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Nom de l'entreprise" className={selectedCompany ? "bg-green-50 border-green-300" : ""} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>SIRET (14 chiffres)</Label>
                                    <Input name="siret" value={formData.siret} onChange={handleChange} placeholder="12345678901234" maxLength={14} className={selectedCompany ? "bg-green-50 border-green-300" : ""} />
                                </div>
                                <div className="space-y-2">
                                    <Label>SIREN (9 chiffres)</Label>
                                    <Input name="siren" value={formData.siren} onChange={handleChange} placeholder="123456789" maxLength={9} className={selectedCompany ? "bg-green-50 border-green-300" : ""} />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2"><Label>Code NAF/APE</Label><Input name="naf_code" value={formData.naf_code} onChange={handleChange} placeholder="Ex: 01.11Z" className={selectedCompany ? "bg-green-50 border-green-300" : ""} /></div>
                                <div className="space-y-2"><Label>Site web</Label><Input name="website" value={formData.website} onChange={handleChange} placeholder="https://..." /></div>
                            </div>
                            <div className="space-y-2">
                                <Label>Description du site/établissement</Label>
                                <Input 
                                    name="site_description" 
                                    value={formData.site_description} 
                                    onChange={handleChange} 
                                    placeholder="Ex: Siège social Paris, Agence Lyon, Entrepôt Marseille..." 
                                />
                                <p className="text-xs text-slate-500">Utile pour différencier plusieurs sites d'une même société</p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Contact Principal */}
                    <Card>
                        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2"><User className="w-5 h-5" />Contact Principal</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2"><Label>Prenom *</Label><Input name="first_name" value={formData.first_name} onChange={handleChange} required data-testid="first-name-input" /></div>
                                <div className="space-y-2"><Label>Nom *</Label><Input name="last_name" value={formData.last_name} onChange={handleChange} required data-testid="last-name-input" /></div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2"><Label>Telephone *</Label><Input name="phone" value={formData.phone} onChange={handleChange} required data-testid="phone-input" /></div>
                                <div className="space-y-2"><Label>Email</Label><Input name="email" type="email" value={formData.email} onChange={handleChange} /></div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Adresse - avec recherche ville par CP */}
                    <Card className="border-2 border-teal-300 bg-gradient-to-r from-teal-50 to-cyan-50">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-teal-700">
                                <MapPinned className="w-5 h-5" />
                                Adresse
                            </CardTitle>
                            <CardDescription className="text-teal-600">
                                Entrez le <strong>code postal</strong> pour trouver automatiquement la ville
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2"><Label>Adresse ligne 1</Label><Input name="address" value={formData.address} onChange={handleChange} className={selectedCompany ? "bg-green-50 border-green-300" : ""} /></div>
                            <div className="space-y-2"><Label>Adresse ligne 2</Label><Input name="address2" value={formData.address2} onChange={handleChange} placeholder="Batiment, etage, etc." /></div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Code Postal
                                        {loadingCities && <Loader2 className="w-4 h-4 animate-spin text-teal-500" />}
                                    </Label>
                                    <Input 
                                        name="postal_code" 
                                        value={formData.postal_code} 
                                        onChange={handleChange} 
                                        maxLength={5} 
                                        placeholder="Ex: 75001"
                                        className={cn("text-lg font-medium", selectedCompany ? "bg-green-50 border-green-300" : "border-teal-300")}
                                        data-testid="postal-code-input" 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Ville</Label>
                                    {citiesForPostalCode.length > 1 ? (
                                        <Select value={formData.city} onValueChange={(v) => handleSelectChange('city', v)}>
                                            <SelectTrigger className="border-teal-300 bg-teal-50"><SelectValue placeholder="Choisir la ville" /></SelectTrigger>
                                            <SelectContent>
                                                {citiesForPostalCode.map((c) => (
                                                    <SelectItem key={c.name} value={c.name}>
                                                        {c.name} {c.population && <span className="text-slate-400 text-xs">({c.population?.toLocaleString()} hab.)</span>}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Input 
                                            name="city" 
                                            value={formData.city} 
                                            onChange={handleChange} 
                                            className={cn(selectedCompany || citiesForPostalCode.length === 1 ? "bg-green-50 border-green-300" : "")}
                                            data-testid="city-input" 
                                        />
                                    )}
                                    {citiesForPostalCode.length === 1 && (
                                        <p className="text-xs text-green-600 flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" /> Ville trouvee automatiquement
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2"><Label>Pays</Label><Input name="country" value={formData.country} onChange={handleChange} /></div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Notes */}
                    <Card>
                        <CardHeader className="pb-3"><CardTitle>Notes</CardTitle></CardHeader>
                        <CardContent><Textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} placeholder="Informations supplementaires..." /></CardContent>
                    </Card>

                    {/* Submit */}
                    <div className="flex justify-end gap-4 pt-4 pb-8">
                        <Button type="button" variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
                        <Button type="submit" className="bg-slate-900 hover:bg-slate-800 px-8" disabled={saving || (!isEditing && !formData.create_easy_power && !formData.create_chantier)} data-testid="save-prospect-btn">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                            {isEditing ? 'Enregistrer' : 'Creer le Prospect'}
                        </Button>
                    </div>
                </form>
            </div>
        </DashboardLayout>
    );
}
